import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import * as debug from 'debug';
import * as _ from 'lodash';
import * as moment from 'moment';
import * as turf from '@turf/turf';

import { GeolocationProvider } from '../geolocation/geolocation';
import { CrudApiProvider } from '../crud-api/crud-api';
import { GoogleProvider } from '../google/google';
import { TripService } from './trip-service';
import { ModalController } from 'ionic-angular';
import { Platform } from 'ionic-angular';
import { ToolsProvider } from '../tools/tools';

@Injectable()
export class PlaceServiceProvider {
  log = debug('app:PlaceServiceProvider');

  currentLocation; //users location
  placesSearchText = '';

  places = [];
  activeDetailIndex: any;
  // placesCount = 0;
  subs: any[] = [];
  showingLayer = false;

  constructor(
    public http: HttpClient,
    public geolocation: GeolocationProvider,
    public crudApi: CrudApiProvider,
    public googleService: GoogleProvider,
    public tripService: TripService,
    public modalCtrl: ModalController,
    public platform: Platform,
    public tools: ToolsProvider
  ) {
    this.log('constructed');
    this.tripService.placeService = this;
    this.platform.ready().then(() => {
      this.updateCurrentLocation();
    });
  }

  ngOnInit() {}

  ngOnDestroy() {
    _.each(this.subs, s => {
      if (s instanceof Function) {
        s();
      } else if (s.unsubscribe) {
        s.unsubscribe();
      }
    });
  }

  setPlaces(places, fromStartingCoords = null, sort = true) {
    /*
    Used in the map component
    if on a route, set places
    */
    this.log('setting places: ', this.places);
    // this.placesCount = 0;
    if (sort && fromStartingCoords) {
      this.sortPlacesByDistance(fromStartingCoords, places);
    } else {
      this.places = places;
    }
    // this.getGooglePlaceDataForNext(0);
  }

  getGooglePlaceDataForNext(num, buff = 2, places?: any[]) {
    /*
    Get groups of place data from Google Service
    */
    num = num || 0;
    this.log('getGooglePlaceData: ', num, buff);
    _.each(places || this.places, (place, idx) => {
      if (idx < num - buff) {
        return;
      }
      if (idx > num + buff) {
        return;
      }
      if (place.googlePlaceData || place.queryingGooglePlaceData) {
        if (place.__hiddenImage) {
          place.image = place.__hiddenImage;
        }
        return;
      }
      place.queryingGooglePlaceData = true;
      this.log('getting data', idx);
      this.addGooglePlaceDataToPlace(place);
    });
  }

  addGooglePlaceDataToPlace(place) {
    this.log('getGooglePlaceDetail');
    this.log(' place: ', place);
    // get place id
    const googlePlaceId = _.get(place, 'locationMetadata.googlePlaceId');
    this.log('google place id: ', googlePlaceId);
    place.loadingPlaceData = true;

    this.checkDefaultPhoto(place);

    if (!googlePlaceId) {
      place.loadingPlaceData = false;
      return Promise.resolve();
    }

    // Google Service
    return this.googleService.getPlace(googlePlaceId).then(
      googleRes => {
        place.loadingPlaceData = false;
        this.log('google response: ', googleRes);
        if (googleRes && _.get(googleRes, 'open_now')) {
          delete googleRes.open_now;
        }
        place.googlePlaceData = googleRes;

        // get today's hours
        const numDayOfWeek = moment().day() - 1;
        place.todaysHours = _.nth(
          _.get(googleRes, 'opening_hours.weekday_text'),
          numDayOfWeek
        );

        if (!place.image) {
          try {
            const previewPhoto = _.get(place, 'photos.previewPhoto');

            let photoUrl;

            _.each(_.get(googleRes, 'photos'), photo => {
              photoUrl = photo.getUrl({
                maxWidth: 600,
                maxHeight: 600,
              });

              // nothing specified, no reason for more work
              if (!previewPhoto) {
                return false;
              }

              const id = _.get(photo, 'id');

              if (id === previewPhoto) {
                return false;
              }
            });

            if (photoUrl) {
              place.image = photoUrl;
            }
          } catch (error) {
            this.log('Error getting google image: ', error);
          }
        }
      },
      e => {
        place.loadingPlaceData = false;
      }
    );
  }

  checkDefaultPhoto(place) {
    const image = _.find(_.get(place, 'photos.uploadedPhotos'), p => {
      return p && p.url && p.url === _.get(place, 'photos.previewPhoto');
    });

    if (image && image.url) {
      place.image = image.url;
    }
  }

  openStopDetailPageForPlace(place?, options?) {
    let opts = place ? { place: place } : {};
    opts = _.merge(opts, options);
    this.log('navparams openPlace', _.cloneDeep(opts));

    let modal;

    if (localStorage.getItem('TEST_PlaceNotificationPage')) {
      modal = this.modalCtrl.create('PlaceNotificationPage', opts);
    } else {
      modal = this.modalCtrl.create('StopDetailPage', opts);
    }

    modal.onDidDismiss(d => {});

    modal.present();
  }

  private updateCurrentLocation() {
    this.geolocation.on('updateUserLocation', () => {
      this.geolocation.getRecentPosition().then(res => {
        this.log('CURRENTLOCATION ', res);
        this.currentLocation = {
          name: 'Current Location',
          description: '',
          placeType: 'CURRENT_LOCATION',
          location: {
            type: 'Point',
            coordinates: [res.longitude, res.latitude],
          },
        };
      });
    });
  }

  private sortPlacesByDistance(startCoords, places) {
    const route = this.tripService.getActiveRoute();

    if (!route) {
      return;
    }

    _.forEach(places, place => {
      place.distance = this.getDistanceFromPoint(place, route);
    });

    this.places = _.sortBy(places, ['distance']);
    this.log('sortedPlacesByDistance: ', this.places);
  }

  private getDistanceFromPoint(place, route) {
    const pointOnLine = turf.nearestPointOnLine(
      route,
      {
        type: 'Feature',
        properties: {},
        geometry: place.location,
      },
      { units: 'miles' }
    );
    return _.get(pointOnLine, 'properties.location', 100000);
  }

  locationSearch(opts = {}) {
    if (window.event) {
      event.stopPropagation();
      event.preventDefault();
    }

    return new Promise<void>((resolve, reject) => {
      const modal = this.modalCtrl.create('LocationSearchPage', {
        options: opts,
      });

      modal.onDidDismiss(d => {
        // d does not apply to Google Search
        // if not Google search, never resolve on cancel
        if (d) {
          resolve(d);
        } else {
          resolve();
        }
      });

      modal.present();
    });
  }

  getPlacesNearby(location = null, type = null): Promise<any> {
    return new Promise((resolve, reject) => {
      resolve(this.googleService.getNearbyPlaces(location, type));
    });
  }
  getReviewsStatsForPlace(placeId) {
    return this.crudApi
      .get(`/api/reviews`, { place: placeId })
      .then(res => {
        _.remove(res.data, r => {
          return !this.tools.validateReviewForDisplay(r);
        });
        let stat = {
          reviewCount: 0,
          average: 0,
        };
        stat.reviewCount = _.size(res.data);
        let recommended = 0,
          notRecommended = 0;
        _.each(res.data, r => {
          if (r.recommended === true) {
            recommended++;
          } else if (r.recommended === false) {
            notRecommended++;
          }
        });
        const totalReviews = recommended + notRecommended;
        if (recommended === totalReviews) {
          stat.average = 5.0;
        } else if (recommended < totalReviews) {
          stat.average = _.round(
            _.clamp(_.divide(recommended, totalReviews), 0, 1) * 5.0,
            2
          );
        }
        return stat;
      })
      .catch(e => {
        return {
          reviewCount: 0,
          average: 0,
        };
      });
  }
}



// WEBPACK FOOTER //
// ./src/providers/trip-service/place-service.ts